import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  Code,
  CopyButton,
  Group,
  Modal,
  SegmentedControl,
  Select,
  Skeleton,
  Stack,
  Table,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useDebouncedValue } from "@mantine/hooks";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router";
import type {
  AnalyticsAd,
  AnalyticsCredential,
  AnalyticsPlatform,
  PaginatedResponse,
} from "../../../api.ts";
import { ADMIN_LIST_PAGE_SIZE, adminErrorMessage, adminGet, adminSend } from "../../../api.ts";
import { notifyAdminError, notifyAdminSuccess } from "../../../lib/admin-notify.ts";
import { CursorPagination } from "../../../components/CursorPagination.tsx";
import { BotTimezoneNotice } from "../../../components/BotTimezoneNotice.tsx";
import { BusinessDateTime } from "../../../components/BusinessDateTime.tsx";
import { todayInTz } from "@mip-tg-bot-turntable/utils";
import { AdminTable } from "../../../components/AdminTable.tsx";
import { botSectionHref } from "../../../lib/bot-nav.ts";
import { ANALYTICS_PLATFORM_SEGMENTS } from "./platform-segments.ts";

const AD_PLATFORMS = new Set<string>(ANALYTICS_PLATFORM_SEGMENTS.map((p) => p.value));

type AdsListResponse = PaginatedResponse<AnalyticsAd> & {
  readonly platformCounts?: {
    readonly facebook: number;
    readonly tiktok: number;
    readonly google: number;
    readonly sharechat: number;
  };
};

const PAGE_SIZE = ADMIN_LIST_PAGE_SIZE;

const SHORTLINK_BASE = "https://tg.deioio.com/s/";

// 与 packages/domain 的 SHARECHAT_EVENT_NAMES 一致；frontend-admin 不依赖 domain 包
const SHARECHAT_EVENT_NAMES = [
  "page_visits",
  "registration",
  "app_download",
  "install",
  "transaction",
];

/** 构造投放 URL 模板：chatToken 留占位，click id 为平台宏；Google 带 customer_id 固定占位 */
function buildUrlTemplate(ad: AnalyticsAd, googleCustomerId?: string | null): string {
  const base = `${SHORTLINK_BASE}{chatToken}`;
  const adParam = `ad_id=${encodeURIComponent(ad.adId)}`;
  if (ad.platform === "facebook") return `${base}?${adParam}&fbclid={{fbclid}}`;
  if (ad.platform === "tiktok") return `${base}?${adParam}&ttclid={{ttclid}}`;
  if (ad.platform === "sharechat") {
    return `${base}?${adParam}&clickId=%%DELIVERY_IDENTIFIER%%&campaignname=%%CAMPAIGN_NAME%%&gaid=%%ADVERTISER_IDENTIFIER%%&userId=%%PUBLISHER_IDENTIFIER%%&p=sharechat&advid=<AppName>`;
  }
  const cid =
    googleCustomerId !== null && googleCustomerId !== undefined && googleCustomerId !== ""
      ? googleCustomerId
      : "<customer_id>";
  return [
    `搜索/展示: ${base}?${adParam}&gclid={gclid}&customer_id=${cid}`,
    `iOS W2A:   ${base}?${adParam}&wbraid={wbraid}&customer_id=${cid}`,
    `iOS A2A:   ${base}?${adParam}&gbraid={gbraid}&customer_id=${cid}`,
  ].join("\n");
}

interface GoogleReportCheck {
  ready: boolean;
  credentialId: string | null;
  conversionActionId: string | null;
  pendingJoinCount: number;
  withClickIdCount: number;
  requeuedFailedOrSkipped: number;
  requeuedSent: number;
  ctidChanged: boolean;
  blockers: string[];
}

const BLOCKER_LABEL: Record<string, string> = {
  no_credential: "缺 Google 凭据（投放 URL 带 customer_id 或手选凭据）",
  no_ctid: "缺 Conversion Action ID (ctId)",
  no_pending: "暂无 pending 转化可上报",
  no_gclid: "pending 转化无关联 gclid/wbraid/gbraid",
  ad_deleted: "广告已软删（先复活再上报）",
};

interface CreateAdForm {
  platform: AnalyticsPlatform | "";
  adId: string;
  eventName: string;
  remark: string;
  credentialId: string;
  googleConversionActionId: string;
}

interface EditAdForm {
  eventName: string;
  googleConversionActionId: string;
  credentialId: string;
}

export function AdsTab(props: { readonly botId: string; readonly timezone: string }) {
  const { botId, timezone } = props;
  const qc = useQueryClient();
  const navigate = useNavigate();
  // splat：ads 或 ads/sharechat（与平台凭据同款子路径）
  const { "*": splat = "" } = useParams<{ "*": string }>();
  const splatParts = splat.split("/").filter(Boolean);
  const platformFromPath =
    splatParts[0] === "ads" && splatParts[1] !== undefined && AD_PLATFORMS.has(splatParts[1])
      ? (splatParts[1] as AnalyticsPlatform)
      : null;

  const [platform, setPlatform] = useState<AnalyticsPlatform>(platformFromPath ?? "facebook");
  const [editOpen, setEditOpen] = useState<AnalyticsAd | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [remarkEdit, setRemarkEdit] = useState<AnalyticsAd | null>(null);
  const [remarkDraft, setRemarkDraft] = useState("");
  const [adIdInput, setAdIdInput] = useState("");
  const [debouncedAdId] = useDebouncedValue(adIdInput, 300);
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [cursors, setCursors] = useState<Array<string | null>>([null]);
  const [pageIndex, setPageIndex] = useState(0);
  const cursor = cursors[pageIndex] ?? null;

  // URL -> 状态；仅 /ads 时补默认平台段
  useEffect(() => {
    if (splatParts[0] === "ads" && platformFromPath === null) {
      void navigate(botSectionHref(botId, `ads/${platform}`), { replace: true });
      return;
    }
    if (platformFromPath !== null && platformFromPath !== platform) {
      setPlatform(platformFromPath);
    }
  }, [platformFromPath, platform, splatParts, botId, navigate]);

  const resetPages = () => {
    setCursors([null]);
    setPageIndex(0);
  };

  const switchPlatform = (p: AnalyticsPlatform) => {
    setPlatform(p);
    setAdIdInput("");
    resetPages();
    void navigate(botSectionHref(botId, `ads/${p}`), { replace: true });
  };

  /** Google ad_id / ctId：8-19 位纯数字（与 domain parseGoogle* 一致） */
  const googleNumericId = (v: string, label: string): string | null => {
    const t = v.trim();
    if (t === "") return null;
    if (!/^\d{8,19}$/.test(t)) return `${label} 须为 8-19 位纯数字（勿填宏或带横线账号）`;
    return null;
  };

  const form = useForm<EditAdForm>({
    initialValues: { eventName: "", googleConversionActionId: "", credentialId: "" },
    validate: {
      googleConversionActionId: (v) => googleNumericId(v, "ctId"),
    },
  });

  const createForm = useForm<CreateAdForm>({
    initialValues: {
      platform: "facebook",
      adId: "",
      eventName: "",
      remark: "",
      credentialId: "",
      googleConversionActionId: "",
    },
    validate: {
      platform: (v) => (v === "" ? "请选择平台" : null),
      adId: (v, values) => {
        if (v.trim() === "") return "广告 ID 必填（与投放 URL 宏一致）";
        if (values.platform === "google") return googleNumericId(v, "广告 ID");
        return null;
      },
      googleConversionActionId: (v, values) =>
        values.platform === "google" ? googleNumericId(v, "ctId") : null,
    },
  });

  const credList = useQuery({
    queryKey: ["bot", botId, "analytics-credentials"],
    queryFn: () => adminGet<{ items: AnalyticsCredential[] }>(`/analytics/${botId}/credentials`),
    refetchInterval: 60_000,
  });
  const googleCreds = (credList.data?.items ?? []).filter(
    (c) => c.platform === "google" && c.isEnabled,
  );
  const googleCredLabel = (credentialId: string | null): string => {
    if (credentialId === null) return "";
    const c = googleCreds.find((x) => x.id === credentialId);
    if (c === undefined) return credentialId.slice(0, 8);
    const remark = c.remark !== null && c.remark !== "" ? ` · ${c.remark}` : "";
    return `${c.googleOperatingCustomerId ?? "?"} · ${c.googleSaEmail ?? "?"}${remark}`;
  };
  const googleCustomerIdForAd = (row: AnalyticsAd): string | null => {
    if (row.credentialId === null) return null;
    const c = googleCreds.find((x) => x.id === row.credentialId);
    return c?.googleOperatingCustomerId ?? null;
  };

  const list = useQuery({
    queryKey: ["bot", botId, "analytics-ads", debouncedAdId, platform, includeDeleted, cursor],
    queryFn: () => {
      const params = new URLSearchParams();
      params.set("pageSize", String(PAGE_SIZE));
      if (cursor !== null) params.set("cursor", cursor);
      if (debouncedAdId.trim() !== "") params.set("adId", debouncedAdId.trim());
      params.set("platform", platform);
      if (includeDeleted) params.set("includeDeleted", "1");
      return adminGet<AdsListResponse>(`/analytics/${botId}/ads?${params.toString()}`);
    },
    staleTime: 30_000,
  });

  const countBy = useMemo(() => {
    const empty = { facebook: 0, tiktok: 0, google: 0, sharechat: 0 };
    return { ...empty, ...list.data?.platformCounts };
  }, [list.data?.platformCounts]);

  const patch = useMutation({
    mutationFn: (args: { id: string; eventName: string | null }) =>
      adminSend("PATCH", `/analytics/${botId}/ads/${args.id}`, {
        eventName: args.eventName,
      }),
    onSuccess: () => {
      notifyAdminSuccess("已更新 event name");
      setEditOpen(null);
      form.reset();
      void qc.invalidateQueries({ queryKey: ["bot", botId, "analytics-ads"] });
    },
    onError: (e) => notifyAdminError(e, "保存失败"),
  });

  const patchGoogle = useMutation({
    mutationFn: (args: {
      id: string;
      credentialId?: string | null;
      googleConversionActionId: string | null;
    }) => {
      const body: Record<string, unknown> = {
        googleConversionActionId: args.googleConversionActionId,
      };
      // 未选手动凭据时不传 credentialId，保留 shortlink customer_id 自动 pin 的值
      if (args.credentialId !== undefined) {
        body["credentialId"] = args.credentialId;
      }
      return adminSend<{ ok: true; reportCheck: GoogleReportCheck }>(
        "PATCH",
        `/analytics/${botId}/ads/${args.id}/google`,
        body,
      );
    },
    onSuccess: (resp) => {
      const rc = resp.reportCheck;
      const reqParts: string[] = [];
      if (rc.requeuedFailedOrSkipped > 0) reqParts.push(`失败/跳过 ${rc.requeuedFailedOrSkipped}`);
      if ((rc.requeuedSent ?? 0) > 0) reqParts.push(`已上报重推 ${rc.requeuedSent}`);
      const requeueMsg = reqParts.length > 0 ? `（已重入队：${reqParts.join("，")}）` : "";
      const ctidHint = rc.ctidChanged
        ? " ctId 已变更：原 sent 将按新 ctId 再推（旧 Ads 转化不会自动撤销）。"
        : "";
      if (rc.ready) {
        notifications.show({
          color: "green",
          message: `配置已保存。上报就绪：${rc.withClickIdCount}/${rc.pendingJoinCount} 条 pending 带 click id，将由 cron 上报${requeueMsg}${ctidHint}`,
          autoClose: 10000,
        });
      } else {
        const why = rc.blockers.map((b) => BLOCKER_LABEL[b] ?? b).join("；") || "条件未齐";
        notifications.show({
          color: "yellow",
          message: `配置已保存，但暂不可上报：${why}${requeueMsg}${ctidHint}`,
          autoClose: 12000,
        });
      }
      setEditOpen(null);
      form.reset();
      void qc.invalidateQueries({ queryKey: ["bot", botId, "analytics-ads"] });
      void qc.invalidateQueries({ queryKey: ["bot", botId, "analytics-conversions"] });
    },
    onError: (e) => notifyAdminError(e, "保存失败"),
  });

  const createAd = useMutation({
    mutationFn: (v: CreateAdForm) => {
      // 平台锁定当前 Tab，禁止弹窗里再下拉改平台
      const plat = platform;
      const body: Record<string, unknown> = {
        platform: plat,
        adId: v.adId.trim(),
        remark: v.remark.trim() === "" ? null : v.remark.trim(),
      };
      if (plat === "google") {
        body["credentialId"] = v.credentialId === "" ? null : v.credentialId;
        body["googleConversionActionId"] =
          v.googleConversionActionId.trim() === "" ? null : v.googleConversionActionId.trim();
      } else {
        body["eventName"] = v.eventName.trim() === "" ? null : v.eventName.trim();
      }
      return adminSend<AnalyticsAd>("POST", `/analytics/${botId}/ads`, body);
    },
    onSuccess: (row) => {
      notifyAdminSuccess(`已预录入 ${row.platform} / ${row.adId}`, "保存成功");
      setCreateOpen(false);
      createForm.reset();
      createForm.setFieldValue("platform", platform);
      void qc.invalidateQueries({ queryKey: ["bot", botId, "analytics-ads"] });
    },
    onError: (e) => notifyAdminError(e, "保存失败"),
  });

  const saveRemark = useMutation({
    mutationFn: (args: { id: string; remark: string }) =>
      adminSend("PATCH", `/analytics/${botId}/ads/${args.id}`, {
        remark: args.remark.trim() === "" ? null : args.remark.trim().slice(0, 64),
      }),
    onSuccess: () => {
      notifyAdminSuccess("广告备注已保存");
      setRemarkEdit(null);
      void qc.invalidateQueries({ queryKey: ["bot", botId, "analytics-ads"] });
    },
    onError: (e) => notifyAdminError(e, "保存失败"),
  });

  const softDeleteAd = useMutation({
    mutationFn: (id: string) => adminSend<void>("DELETE", `/analytics/${botId}/ads/${id}`),
    onSuccess: () => {
      notifyAdminSuccess("已软删广告（转化历史保留；新点击会自动复活）");
      void qc.invalidateQueries({ queryKey: ["bot", botId, "analytics-ads"] });
    },
    onError: (e) => notifyAdminError(e, "删除失败"),
  });

  const restoreAd = useMutation({
    mutationFn: (id: string) => adminSend<void>("POST", `/analytics/${botId}/ads/${id}/restore`),
    onSuccess: () => {
      notifyAdminSuccess("广告已复活");
      void qc.invalidateQueries({ queryKey: ["bot", botId, "analytics-ads"] });
    },
    onError: (e) => notifyAdminError(e, "操作失败"),
  });

  const openEdit = (row: AnalyticsAd) => {
    setEditOpen(row);
    form.setValues({
      eventName: row.eventName ?? "",
      googleConversionActionId: row.googleConversionActionId ?? "",
      credentialId: row.credentialId ?? "",
    });
  };

  const openCreate = () => {
    createForm.reset();
    createForm.setFieldValue("platform", platform);
    setCreateOpen(true);
  };

  const rows = (list.data?.items ?? []).map((row) => {
    const urlTemplate = buildUrlTemplate(
      row,
      row.platform === "google" ? googleCustomerIdForAd(row) : null,
    );
    const adRemark = row.remark?.trim() ?? "";
    const isDeleted = row.deletedAt !== null && row.deletedAt !== undefined;
    const googleReady =
      row.platform === "google" &&
      !isDeleted &&
      row.googleConversionActionId !== null &&
      row.googleConversionActionId !== "" &&
      row.credentialId !== null;
    return (
      <Table.Tr key={row.id} opacity={isDeleted ? 0.55 : 1}>
        <Table.Td>
          <Group gap={6} wrap="wrap">
            <Code>{row.adId}</Code>
            {isDeleted ? (
              <Badge size="xs" color="gray" variant="outline">
                已删除
              </Badge>
            ) : null}
          </Group>
        </Table.Td>
        <Table.Td>
          <Text size="sm" lineClamp={1} title={adRemark || undefined}>
            {adRemark !== "" ? adRemark : "无"}
          </Text>
        </Table.Td>
        <Table.Td>
          {row.platform === "google" ? (
            <Stack gap={2}>
              {googleReady ? (
                <Badge size="xs" color="green" variant="light">
                  已配 ctId
                </Badge>
              ) : (
                <Badge size="xs" color="orange" variant="light">
                  缺凭据/ctId
                </Badge>
              )}
              <Text size="xs" c="dimmed" lineClamp={1}>
                ctId: {row.googleConversionActionId ?? "未填"}
              </Text>
              <Text size="xs" c="dimmed" lineClamp={1}>
                凭据: {row.credentialId !== null ? googleCredLabel(row.credentialId) : "未选"}
              </Text>
            </Stack>
          ) : row.eventName !== null ? (
            <Text size="sm">{row.eventName}</Text>
          ) : (
            <Text size="sm" c="dimmed">
              (继承 bot 默认)
            </Text>
          )}
        </Table.Td>
        <Table.Td>{row.conversionCount.toLocaleString()}</Table.Td>
        <Table.Td>
          <Group gap="xs" wrap="nowrap" align="flex-start">
            <Tooltip label={urlTemplate} multiline maw={420} withArrow>
              <Code
                className="admin-code-wrap"
                style={{
                  fontSize: 11,
                  maxWidth: 280,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-all",
                  overflowWrap: "anywhere",
                  display: "block",
                  lineHeight: 1.35,
                }}
              >
                {urlTemplate}
              </Code>
            </Tooltip>
            <CopyButton value={urlTemplate}>
              {({ copied, copy }) => (
                <Button size="xs" variant="subtle" onClick={copy}>
                  {copied ? "已复制" : "复制"}
                </Button>
              )}
            </CopyButton>
          </Group>
        </Table.Td>
        <Table.Td>
          <BusinessDateTime iso={row.firstSeenAt} businessTimezone={timezone} />
        </Table.Td>
        <Table.Td>
          <Group gap="xs" wrap="nowrap">
            <Button size="xs" variant="outline" onClick={() => openEdit(row)} disabled={isDeleted}>
              {row.platform === "google" ? "配 Google 上报" : "改 event name"}
            </Button>
            <Button
              size="xs"
              variant="light"
              onClick={() => {
                setRemarkEdit(row);
                setRemarkDraft(row.remark ?? "");
              }}
            >
              备注
            </Button>
            {isDeleted ? (
              <Button
                size="xs"
                variant="light"
                color="teal"
                loading={restoreAd.isPending && restoreAd.variables === row.id}
                onClick={() => restoreAd.mutate(row.id)}
              >
                复活
              </Button>
            ) : (
              <Button
                size="xs"
                variant="light"
                color="red"
                loading={softDeleteAd.isPending && softDeleteAd.variables === row.id}
                onClick={() => {
                  modals.openConfirmModal({
                    title: "软删广告",
                    children: (
                      <Text size="sm">
                        列表隐藏该广告，转化/点击历史保留。新流量点同 ad_id 会自动复活。确认继续？
                      </Text>
                    ),
                    labels: { confirm: "删除", cancel: "取消" },
                    confirmProps: { color: "red" },
                    onConfirm: () => softDeleteAd.mutate(row.id),
                  });
                }}
              >
                删除
              </Button>
            )}
          </Group>
        </Table.Td>
      </Table.Tr>
    );
  });

  return (
    <>
      <Stack gap="md">
        <Group justify="space-between" align="flex-start" wrap="wrap" gap="sm">
          <Stack gap={4}>
            <Text fw={600} size="lg">
              广告列表
            </Text>
            <Text size="xs" c="dimmed">
              按平台查看/预录入广告；Google 须配 ctId。投放 URL 在「频道/群聊」复制。
            </Text>
            <BotTimezoneNotice timezone={timezone} statsDay={todayInTz(timezone)} />
          </Stack>
          <Button size="sm" onClick={openCreate}>
            预录入{ANALYTICS_PLATFORM_SEGMENTS.find((p) => p.value === platform)?.label ?? ""}广告
          </Button>
        </Group>

        <SegmentedControl
          fullWidth
          value={platform}
          onChange={(v) => switchPlatform(v as AnalyticsPlatform)}
          data={ANALYTICS_PLATFORM_SEGMENTS.map((p) => ({
            value: p.value,
            label: `${p.label} (${countBy[p.value]})`,
          }))}
        />

        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Group justify="space-between" mb="md" wrap="wrap" gap="sm">
            <Group gap="sm">
              <TextInput
                size="xs"
                placeholder="按广告 ID 筛选"
                value={adIdInput}
                onChange={(e) => {
                  setAdIdInput(e.currentTarget.value);
                  resetPages();
                }}
                style={{ width: 220 }}
              />
              <Checkbox
                size="xs"
                label="含已删除"
                checked={includeDeleted}
                onChange={(e) => {
                  setIncludeDeleted(e.currentTarget.checked);
                  resetPages();
                }}
              />
            </Group>
          </Group>
          {list.isLoading ? (
            <Stack gap="xs">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} h={40} radius="sm" />
              ))}
            </Stack>
          ) : list.isError ? (
            <Text c="red" size="sm" ta="center" py="lg">
              {adminErrorMessage(list.error)}
            </Text>
          ) : (list.data?.items ?? []).length === 0 ? (
            <Stack align="center" gap="sm" py="xl">
              <Text c="dimmed" size="sm" ta="center">
                {debouncedAdId.trim() !== ""
                  ? "没有匹配该广告 ID 的记录"
                  : `暂无 ${ANALYTICS_PLATFORM_SEGMENTS.find((p) => p.value === platform)?.label ?? ""} 广告。可预录入，或等用户首次点击短链自动生成。`}
              </Text>
              {debouncedAdId.trim() === "" && (
                <Button size="sm" onClick={openCreate}>
                  预录入广告
                </Button>
              )}
            </Stack>
          ) : (
            <>
              <AdminTable minWidth={1000}>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>广告 ID</Table.Th>
                    <Table.Th>备注</Table.Th>
                    <Table.Th>上报配置</Table.Th>
                    <Table.Th>转化数</Table.Th>
                    <Table.Th>投放 URL 模板</Table.Th>
                    <Table.Th>首见/预录时间</Table.Th>
                    <Table.Th>操作</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {rows.length === 0 ? (
                    <Table.Tr>
                      <Table.Td colSpan={7}>
                        <Text c="dimmed" size="sm" ta="center">
                          暂无{" "}
                          {ANALYTICS_PLATFORM_SEGMENTS.find((p) => p.value === platform)?.label}{" "}
                          广告
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  ) : (
                    rows
                  )}
                </Table.Tbody>
              </AdminTable>
              <CursorPagination
                pageSize={list.data?.pageSize ?? PAGE_SIZE}
                itemCount={list.data?.items.length ?? 0}
                hasMore={list.data?.hasMore === true}
                canGoPrev={pageIndex > 0}
                loading={list.isFetching}
                onPrev={() => setPageIndex((i) => Math.max(0, i - 1))}
                onNext={() => {
                  const next = list.data?.nextCursor ?? null;
                  if (next === null) return;
                  setCursors((prev) => {
                    const head = prev.slice(0, pageIndex + 1);
                    return [...head, next];
                  });
                  setPageIndex((i) => i + 1);
                }}
              />
            </>
          )}
        </Card>
      </Stack>

      <Modal
        title="预录入广告"
        opened={createOpen}
        onClose={() => {
          setCreateOpen(false);
          createForm.reset();
        }}
        size="lg"
        keepMounted={false}
      >
        <form onSubmit={createForm.onSubmit((v) => createAd.mutate(v))}>
          <Stack gap="sm">
            <Alert color="blue" variant="light">
              <Text size="xs">
                当前 Tab 平台：
                <Text span fw={600}>
                  {ANALYTICS_PLATFORM_SEGMENTS.find((p) => p.value === platform)?.label ?? platform}
                </Text>
                。广告 ID 须与投放 URL 里 <Code>ad_id=</Code> 一致。Google 须配 ctId；禁止{" "}
                <Code>{"{creative_id}"}</Code>。
              </Text>
            </Alert>
            {createAd.isError ? (
              <Alert color="red" title="保存失败">
                <Text size="xs" className="admin-error-text">
                  {adminErrorMessage(createAd.error)}
                </Text>
              </Alert>
            ) : null}
            <TextInput
              label="广告 ID (ad_id)"
              placeholder="与投放 URL 宏一致，最长 64"
              description="Google 创意 ID / FB ad.id / TikTok __CID__ / ShareChat %%AD_ID%% 展开值"
              required
              {...createForm.getInputProps("adId")}
            />
            <TextInput
              label="备注（可选）"
              placeholder="如 印度主素材 A"
              maxLength={64}
              {...createForm.getInputProps("remark")}
            />
            {platform === "google" ? (
              <>
                <Select
                  label="Google 凭据（可选）"
                  placeholder={
                    googleCreds.length === 0
                      ? "请先在平台凭据页添加"
                      : "可空：靠 URL customer_id 自动 pin"
                  }
                  description="一般可不选；投放链带 customer_id 会自动绑定"
                  data={googleCreds.map((c) => ({
                    value: c.id,
                    label: `${c.googleSaEmail ?? c.id.slice(0, 8)} · ${c.googleOperatingCustomerId ?? "?"}${c.remark !== null && c.remark !== "" ? ` · ${c.remark}` : ""}`,
                  }))}
                  disabled={googleCreds.length === 0}
                  clearable
                  {...createForm.getInputProps("credentialId")}
                />
                <TextInput
                  label="Conversion Action ID (ctId)（可选，可后补）"
                  placeholder="有转化后再填也行"
                  description="Google Ads -> 目标 -> 转化操作 -> 详情 URL 的 ctId="
                  {...createForm.getInputProps("googleConversionActionId")}
                />
              </>
            ) : platform === "sharechat" ? (
              <Select
                label="事件名 eventName（可选）"
                placeholder="留空则用 registration"
                description="ShareChat 只认这几个标准事件名"
                data={SHARECHAT_EVENT_NAMES}
                clearable
                value={createForm.values.eventName}
                onChange={(v) => createForm.setFieldValue("eventName", v ?? "")}
                error={createForm.errors["eventName"]}
              />
            ) : (
              <TextInput
                label="事件名 event_name（可选）"
                placeholder="留空则用 bot 默认，如 CompleteRegistration"
                {...createForm.getInputProps("eventName")}
              />
            )}
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setCreateOpen(false)}>
                取消
              </Button>
              <Button type="submit" loading={createAd.isPending}>
                保存预录入
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      <Modal
        title={`编辑广告配置 - ${editOpen?.adId ?? ""}`}
        opened={editOpen !== null}
        onClose={() => {
          setEditOpen(null);
          form.reset();
        }}
        keepMounted={false}
      >
        <form
          onSubmit={form.onSubmit((v) => {
            if (editOpen === null) return;
            if (editOpen.platform === "google") {
              const patchArgs: {
                id: string;
                credentialId?: string | null;
                googleConversionActionId: string | null;
              } = {
                id: editOpen.id,
                googleConversionActionId:
                  v.googleConversionActionId.trim() === ""
                    ? null
                    : v.googleConversionActionId.trim(),
              };
              // 仅改选了不同凭据时才写入；空选不传，保留 shortlink customer_id 自动 pin
              if (v.credentialId !== "" && v.credentialId !== (editOpen.credentialId ?? "")) {
                patchArgs.credentialId = v.credentialId;
              }
              patchGoogle.mutate(patchArgs);
            } else {
              patch.mutate({
                id: editOpen.id,
                eventName: v.eventName.trim() === "" ? null : v.eventName.trim(),
              });
            }
          })}
        >
          <Stack gap="sm">
            {patch.isError || patchGoogle.isError ? (
              <Alert color="red" title="保存失败">
                <Text size="xs" className="admin-error-text">
                  {adminErrorMessage(patch.error ?? patchGoogle.error)}
                </Text>
              </Alert>
            ) : null}
            {editOpen?.platform === "google" ? (
              <>
                <Alert color="blue" variant="light">
                  <Text size="xs">
                    主责补 <Code>ctId</Code>。凭据通常由投放 URL 的 <Code>customer_id</Code> 自动
                    pin；仅在需要改绑时手选。保存后会检查 pending 转化是否可上报。
                  </Text>
                </Alert>
                <Select
                  label="Google 凭据（可选）"
                  placeholder={
                    googleCreds.length === 0
                      ? "请先在平台凭据页添加"
                      : editOpen.credentialId !== null
                        ? "已自动/已绑定（改选才覆盖）"
                        : "未绑定，可手选或靠 customer_id"
                  }
                  description={
                    editOpen.credentialId !== null
                      ? `当前：${googleCredLabel(editOpen.credentialId)}`
                      : "空则保留现有绑定"
                  }
                  data={googleCreds.map((c) => ({
                    value: c.id,
                    label: `${c.googleSaEmail ?? c.id.slice(0, 8)} · ${c.googleOperatingCustomerId ?? "?"}${c.remark !== null && c.remark !== "" ? ` · ${c.remark}` : ""}`,
                  }))}
                  disabled={googleCreds.length === 0}
                  clearable
                  {...form.getInputProps("credentialId")}
                />
                <TextInput
                  label="Conversion Action ID (ctId)"
                  placeholder="Ads 后台 URL 的 ctId 参数，留空清除"
                  description="有 pending 转化时填此 ID 并保存，系统会做上报就绪检查"
                  {...form.getInputProps("googleConversionActionId")}
                />
              </>
            ) : editOpen?.platform === "sharechat" ? (
              <Select
                label="事件名（eventName）"
                placeholder="留空则用 registration"
                description="ShareChat 只认这几个标准事件名"
                data={SHARECHAT_EVENT_NAMES}
                clearable
                value={form.values.eventName}
                onChange={(v) => form.setFieldValue("eventName", v ?? "")}
                error={form.errors["eventName"]}
              />
            ) : (
              <TextInput
                label="事件名（event_name）"
                placeholder="留空则使用 bot 级默认事件名"
                description="例如 Purchase、Lead、CompleteRegistration"
                {...form.getInputProps("eventName")}
              />
            )}
            <Button
              type="submit"
              loading={editOpen?.platform === "google" ? patchGoogle.isPending : patch.isPending}
            >
              保存
            </Button>
          </Stack>
        </form>
      </Modal>

      <Modal
        title="广告备注"
        opened={remarkEdit !== null}
        onClose={() => setRemarkEdit(null)}
        keepMounted={false}
      >
        <Stack gap="sm">
          <Text size="sm" c="dimmed">
            {remarkEdit !== null ? `${remarkEdit.platform} · ${remarkEdit.adId}` : ""}
          </Text>
          <TextInput
            label="备注"
            description="运营可见名称，最长 64 字；清空后保存即删除备注"
            placeholder="如 Dinesh 素材 A / Arrow 主计划"
            value={remarkDraft}
            maxLength={64}
            onChange={(e) => setRemarkDraft(e.currentTarget.value)}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setRemarkEdit(null)}>
              取消
            </Button>
            <Button
              loading={saveRemark.isPending}
              onClick={() => {
                if (remarkEdit === null) return;
                saveRemark.mutate({ id: remarkEdit.id, remark: remarkDraft });
              }}
            >
              保存
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
